import SqftJsonApi from "../../../../../api/sqftJsonApi";
import {SHAPE_LAYOUTS} from "../../../../canvas/core/cabinet/cabinetShapes";
import HandlesLoader from "./cabinetLoader/HandlesLoader";
import CrownMoldingLoader from "./cabinetLoader/CrownMoldingLoader";
import {arePointsDifferent} from "../../../../../helpers/coordinatesHelper";
import workspace from "../../../../canvas/core/workspace";
import {compressMeshForMobileDevices} from "../../../../../helpers/sqft3dArSceneHelper";

class CabinetLoader {
  constructor(scene, modelScale, loader) {
    this.scene = scene;
    this.modelScale = modelScale;
    this.loader = loader;
    this.handlesLoader = new HandlesLoader(this.scene, this.loader);
    this.crownMoldingLoader = new CrownMoldingLoader(this.scene, this.loader, this.modelScale);

    this.cabinetCache = new Map(); // Cache for cabinets
    this.materialCache = {}; // Cache for materials
  }

  async loadModel(cabinetData) {
    if (!cabinetData.url) {
      console.log(`Missing URL for partId: ${cabinetData.partId}`);
      return Promise.resolve(`Missing URL for partId: ${cabinetData.partId}`);
    }

    let urlToLoad = cabinetData.url;

    // Check if the cabinet is resized, framed, or a filler
    const wasShapeResized = cabinetData.contour_points
      && arePointsDifferent(cabinetData.contour_points, cabinetData.original_points);

    if (wasShapeResized || cabinetData.framed || cabinetData.is_filler) {
      try {
        // todo this can be removed after this ticket will be resolved QCDES-6231
        // Adjust shape coordinates before fetching the resized model
        const shape = workspace.getShapeById(cabinetData.shapeId);
        if (shape) {
          shape.rotate(-cabinetData.rotation, shape.Direction.LEFT);
          cabinetData.original_points = shape.getOriginalPointsCoords();
          cabinetData.contour_points = shape.getPointsCoords();
        }

        // Fetch the resized cabinet model
        let resizedCabinet = await this.getResizedCabinetModel(cabinetData);

        // Restore original rotation after fetching coordinates
        if (shape) {
          shape.rotate(cabinetData.rotation, shape.Direction.LEFT);
        }

        if (resizedCabinet && resizedCabinet.s3Url) {
          urlToLoad = resizedCabinet.s3Url;
        }
      } catch (error) {
        console.error(`Error fetching resized model for partId: ${cabinetData.partId}`, error);
      }
    }

    // Check if the model is already cached
    if (this.cabinetCache.has(urlToLoad)) {
      const cachedCabinet = this.cloneCabinet(this.cabinetCache.get(urlToLoad), cabinetData);
      this.scene.add(cachedCabinet);
      return Promise.resolve(cachedCabinet);
    }

    return new Promise((resolve, reject) => {
      this.loader.load(urlToLoad, async (gltf) => {
        const cabinetScene = this.cloneCabinet(gltf.scene, cabinetData);
        this.scene.add(cabinetScene);
        this.cabinetCache.set(urlToLoad, gltf.scene.clone());

        resolve(cabinetScene);
      }, undefined, (error) => {
        console.error(`Error loading model from URL ${urlToLoad}:`, error);
        reject(error);
      });
    });
  }

  // Function to clone and apply material, handles, and lighting
  cloneCabinet(originalScene, cabinetData) {
    const clonedScene = originalScene.clone(true);

    clonedScene.position.set(
      cabinetData.x / this.modelScale,
      cabinetData.y / this.modelScale,
      cabinetData.z / this.modelScale
    );
    clonedScene.rotation.y = window.THREE.MathUtils.degToRad(cabinetData.rotation);

    // compress Mesh For Mobile Devices
    clonedScene.traverse((child) => {
      if (child.isMesh) {
        compressMeshForMobileDevices(child);
      }
    });

    // Apply material
    if (cabinetData.material_url) {
      this.applyCachedMaterial(cabinetData.material_url, clonedScene);
      //this.applyCachedMaterial( '/images/sqft-icons/3d/material/All-Cabinet-Chestnut-Kitchen Magic.glb', clonedScene);
    }

    // Load handles
    if (cabinetData.handles && cabinetData.handles.length > 0) {
      this.handlesLoader.loadHandles(cabinetData, clonedScene);
    }

    // Adjust lighting if necessary
    this.adjustLighting(clonedScene);

    return clonedScene;
  }

  adjustLighting(scene) {
    scene.traverse((child) => {
      if (child.isMesh) {
        child.material.needsUpdate = true;
      }
    });
  }

  async applyCachedMaterial(materialUrl, scene) {
    if (this.materialCache[materialUrl]) {
      this.assignMaterialToScene(scene, this.materialCache[materialUrl]);
    } else {
      this.loader.load(materialUrl, (materialGltf) => {
        const materialMesh = materialGltf.scene.getObjectByProperty('type', 'Mesh');
        if (materialMesh) {
          let originalMaterial = materialMesh.material;
          compressMeshForMobileDevices(materialMesh);

          // Cache optimized material
          this.materialCache[materialUrl] = originalMaterial;

          // Apply optimized material to the scene
          this.assignMaterialToScene(scene, originalMaterial);
        }
      }, undefined, (error) => {
        console.error(`❌ Error loading material ${materialUrl}:`, error);
      });
    }
  }

  assignMaterialToScene(scene, material) {
    scene.traverse((child) => {
      if (child.isMesh
        && !child.name.includes("Drawer_")
        && !child.name.includes("Door_")
        && !child.name.includes("Handle_")
      ) {
        child.material = material;
        const map_name = material.map.name;
        const parts = map_name.split(" - ");
        const floatValues = parts.map(parseFloat).filter(num => !isNaN(num));
        const [v_scale, u_scale] = floatValues.slice(-2);
        const v_offset = Math.random() * 5;
        const u_offset = Math.random() * 5;
        if (child.geometry.attributes) {
          const uv = child.geometry.attributes.uv.array;
          for (let i = 0; i < uv.length; i += 2) {
            uv[i] = u_offset + uv[i] * u_scale;
            uv[i + 1] = v_offset + uv[i + 1] * v_scale;
          }
          child.geometry.attributes.uv.needsUpdate = true;
        }
        child.material.needsUpdate = true;
      }
    });
  }

  async getResizedCabinetModel(position) {
    if (position.shapeLayout !== SHAPE_LAYOUTS.rectangle) {
      return await SqftJsonApi.getResizedCornerCabinet(position, {});
    } else {
      return await SqftJsonApi.getResizedCabinet(position, {});
    }
  }

  drawLineBetweenPoints(start, end) {
    const material = new window.THREE.LineBasicMaterial({ color: 0xff0000 });
    const points = [];
    points.push(new window.THREE.Vector3(start.x, start.y, start.z));
    points.push(new window.THREE.Vector3(end.x, end.y, end.z));

    const geometry = new window.THREE.BufferGeometry().setFromPoints(points);
    const line = new window.THREE.Line(geometry, material);

    this.scene.add(line);
  }
}

export default CabinetLoader;
